import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import styles from './CharacterProfile.module.css';
import { api } from '../../api';
import ProfileLoading from '../utils/ProfileLoading';
import { useAuth } from '../../context/AuthContext';
import { useEditableField } from '../../hooks/useEditableField';
import { FiCheck, FiEdit } from 'react-icons/fi';
import CharacterProfileTabs from './CharacterProfileTabs';
import ShareButton from './ShareButton';
import CharacterProfileHeader from './CharacterProfileHeader';
import CharacterProfileModals from './CharacterProfileModals';
import { CharacterDetailResponse, Visibility, UpdateCharacterPayload } from '../../api/types/chat';
import { CharacterType } from '../../api/characters';

// Add the missing types
type ActiveTab = 'about' | 'history' | 'edit';
type NotificationType = {
  message: string;
  type: 'success' | 'error' | 'info';
};

type ModalConfigType = {
  title: {
    view: string;
    edit: string;
  };
  placeholder: string;
  defaultValue: string;
  rows: number;
  maxLength: number;
};

type ModalStateType = {
  type: string | null;
  mode: 'edit' | 'view' | null;
  loading: boolean;
  error: string;
  value: string;
  config: ModalConfigType | null;
};

interface ChatType {
  id: string;
}

interface CharacterProfileProps {
  chat?: ChatType | null;
}

const USE_TEST_DATA = false;

const TEST_DATA: Partial<CharacterDetailResponse> = {
  icon: "/media/characters/mario.png",
  display_name: "Angry Duolingo",
  username: "AngryDuolingo",
  tagline: "Uh oh you made Duolingo mad",
  author: "twerpyboi34"
};

const CharacterProfile: React.FC<CharacterProfileProps> = ({ chat = null }) => {
  const navigate = useNavigate();
  const { username } = useParams<{ username: string }>();
  const [character, setCharacter] = useState<CharacterType | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ActiveTab>('about');
  const { user } = useAuth();
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const location = useLocation();
  const [notification, setNotification] = useState<NotificationType | null>(null);
  
  // Extract chat ID from location state if available
  const chatId = location.state?.fromChatId || (chat?.id);
  const shouldAutostart = new URLSearchParams(location.search).get('autostart') === 'true';

  // Add a ref to track the last visibility update time
  const lastVisibilityUpdateRef = useRef<number>(0);

  // Helper function to ensure we always have the update method
  const safeUpdate = useCallback(async (data: UpdateCharacterPayload): Promise<CharacterType> => {
    if (!character || !character.update) {
      throw new Error('Character update function not available');
    }
    
    // Call the update function directly from the character object
    const updatedCharacter = await character.update(data);
    
    // Replace the entire character object with the new instance
    setCharacter(updatedCharacter);
    
    return updatedCharacter;
  }, [character]);
  
  // Helper function to ensure we always have the updateVisibility method
  const safeUpdateVisibility = useCallback(async (visibility: Visibility): Promise<CharacterType> => {
    if (!character || !character.updateVisibility) {
      throw new Error('Character updateVisibility function not available');
    }
    
    // Call the update function directly from the character object
    const updatedCharacter = await character.updateVisibility(visibility);
    
    // Replace the entire character object with the new instance
    setCharacter(updatedCharacter);
    
    return updatedCharacter;
  }, [character]);

  const MODAL_CONFIGS: Record<string, ModalConfigType> = {
    description: {
      title: {
        view: 'Description',
        edit: 'Edit Description'
      },
      placeholder: 'Enter character description',
      defaultValue: "This character hasn't written their description yet.",
      rows: 6,
      maxLength: 6250
    },
    external_description: {
      title: {
        view: 'External Description',
        edit: 'Edit External Description'
      },
      placeholder: 'What do other people know about this Character?',
      defaultValue: "No external description available.",
      rows: 4,
      maxLength: 256
    },
    greeting: {
      title: {
        view: 'Greeting',
        edit: 'Edit Greeting'
      },
      placeholder: 'Enter character greeting',
      defaultValue: "Hey there! Nice to meet you! 👋",
      rows: 4,
      maxLength: 2048
    }
  };

  const [modalState, setModalState] = useState<ModalStateType>({
    type: null,
    mode: null,
    loading: false,
    error: '',
    value: '',
    config: null
  });

  const closeModal = (): void => {
    setModalState({
      type: null,
      mode: null,
      loading: false,
      error: '',
      value: '',
      config: null
    });
  };

  const openModal = (type: string, mode: 'edit' | 'view'): void => {
    setModalState({
      type,
      mode,
      loading: false,
      error: '',
      value: character?.[type as keyof CharacterType] as string || '',
      config: MODAL_CONFIGS[type]
    });
  };

  const handleModalSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    
    // Validate against max length
    const maxLength = modalState.config?.maxLength;
    const currentValue = modalState.value.trim();
    
    if (maxLength && currentValue.length > maxLength) {
      setModalState(prev => ({ 
        ...prev, 
        error: `Text exceeds maximum length of ${maxLength} characters`,
      }));
      return;
    }
    
    setModalState(prev => ({ ...prev, loading: true, error: '' }));
    
    try {
      const field = modalState.type;
      if (!field) {
        throw new Error('No field selected for update');
      }
      
      // Update the character and let safeUpdate handle state management
      await safeUpdate({ 
        [field]: currentValue 
      } as UpdateCharacterPayload);
      
      closeModal();
    } catch (error) {
      console.error(`Failed to update ${modalState.type}:`, error);
      setModalState(prev => ({ 
        ...prev, 
        error: `Failed to update ${modalState.type}`,
        loading: false
      }));
    }
  };

  // Function to handle character field changes
  const handleModalValueChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    const newValue = e.target.value;
    const maxLength = modalState.config?.maxLength;
    
    // Prevent typing beyond max length
    if (maxLength && newValue.length > maxLength) {
      return;
    }
    
    setModalState(prev => ({ ...prev, value: newValue }));
  };

  const isCurrentAuthor = user?.username === character?.author;
  const isEditMode = activeTab === 'edit';

  // Update initial values when character changes
  useEffect(() => {
    if (character && modalState.type) {
      setModalState(prev => ({
        ...prev,
        value: character[prev.type as keyof CharacterType] as string || ''
      }));
    }
  }, [character]);

  useEffect(() => {
    if (character) {
      displayName.setValue(character.display_name || '');
      tagline.setValue(character.tagline || '');
      characterIcon.setValue(character.icon || '');
    }
  }, [character]);

  const displayName = useEditableField({
    initialValue: character?.display_name,
    fieldName: 'display name',
    onUpdate: async (newValue: string) => {
      // Check for quotes and strip them if present
      const originalValue = newValue;
      const sanitizedValue = newValue.replace(/['"]/g, '').trim();
      
      // Notify user if quotes were removed
      if (originalValue !== sanitizedValue) {
        setNotification({
          message: 'Quotes were removed from the display name',
          type: 'info'
        });
      }
      
      // Get the fully updated character back
      const updatedCharacter = await safeUpdate({ 
        display_name: sanitizedValue 
      });
      
      // No need to manually update state here as safeUpdate handles it
      return updatedCharacter;
    },
    validate: (value: string) => {
      if (!value.trim()) return 'Display name cannot be empty';
      return undefined;
    },
    isVisible: isCurrentAuthor && isEditMode
  });

  const tagline = useEditableField({
    initialValue: character?.tagline,
    fieldName: 'tagline',
    onUpdate: async (newValue: string) => {
      // Get the fully updated character back
      const updatedCharacter = await safeUpdate({ 
        tagline: newValue.trim() 
      });
      // No need to manually update state here as safeUpdate handles it
      return updatedCharacter;
    },
    isVisible: isCurrentAuthor && isEditMode
  });

  const characterIcon = useEditableField({
    initialValue: character?.icon,
    fieldName: 'icon',
    onUpdate: async (newValue: string) => {
      // Get the fully updated character back
      const updatedCharacter = await safeUpdate({ icon: newValue as unknown as File });
      // No need to manually update state here as safeUpdate handles it
      return updatedCharacter;
    },
    isVisible: isCurrentAuthor && isEditMode
  });

  // Remove the useEditableField for visibility and replace with direct state management
  const [characterVisibilityState, setCharacterVisibilityState] = useState<Visibility>('public');
  const [isUpdatingVisibility, setIsUpdatingVisibility] = useState<boolean>(false);
  const [visibilityError, setVisibilityError] = useState<string>('');

  // Update visibility state when character changes
  useEffect(() => {
    if (character?.visibility) {
      console.log('Character visibility changed in useEffect:', character.visibility);
      setCharacterVisibilityState(character.visibility);
    }
  }, [character?.visibility]);

  // Handle visibility change
  const handleVisibilityChange = async (e: React.ChangeEvent<HTMLSelectElement>): Promise<void> => {
    const newVisibility = e.target.value as Visibility;
    console.log('Updating visibility to:', newVisibility, 'Current visibility:', characterVisibilityState);
    
    // Check if the visibility is actually changing
    if (newVisibility === characterVisibilityState) {
      console.log('Visibility unchanged, skipping update');
      return;
    }
    
    // Debounce visibility changes - prevent rapid consecutive updates
    const now = Date.now();
    const timeSinceLastUpdate = now - lastVisibilityUpdateRef.current;
    if (timeSinceLastUpdate < 2000) { // 2 seconds debounce
      console.log(`Too soon since last update (${timeSinceLastUpdate}ms). Debouncing.`);
      setNotification({
        message: 'Please wait a moment before changing visibility again',
        type: 'info'
      });
      return;
    }
    
    if (newVisibility !== 'public' && newVisibility !== 'private' && newVisibility !== 'unlisted') {
      console.error('Invalid visibility value:', newVisibility);
      setVisibilityError('Invalid visibility value');
      return;
    }

    setIsUpdatingVisibility(true);
    setVisibilityError('');
    
    // Update the last visibility update time
    lastVisibilityUpdateRef.current = now;
    
    // Preemptively update the UI for responsiveness
    const previousVisibility = characterVisibilityState;
    setCharacterVisibilityState(newVisibility);

    try {
      // Try to update using our safe method
      await safeUpdateVisibility(newVisibility);
      
      // Show success notification
      const visibilityLabels: Record<Visibility, string> = {
        'public': 'Public',
        'private': 'Private',
        'unlisted': 'Unlisted'
      };
      
      setNotification({
        message: `Character visibility changed to ${visibilityLabels[newVisibility]}`,
        type: 'success'
      });
    } catch (error) {
      console.error('Failed to update visibility:', error);
      
      // Revert to previous visibility in UI
      setCharacterVisibilityState(previousVisibility);
      
      // Set error for UI feedback
      setVisibilityError('Failed to update visibility');
      
      // Show error notification
      setNotification({
        message: 'Failed to update visibility',
        type: 'error'
      });
    } finally {
      setIsUpdatingVisibility(false);
    }
  };

  const characterUsername = {
    isEditing: false,
    isLoading: false,
    error: null,
    value: character?.username || '',
    setValue: () => {},
    startEdit: () => {},
    setIsEditing: () => {},
    handleSubmit: () => {},
    isVisible: false
  };

  const handleStartChat = async (): Promise<void> => {
    try {
      // If not logged in, navigate to preview mode
      if (!user) {
        // Create a simplified character object with only serializable properties
        if (!character) return;
        
        const serializableCharacter = {
          id: character.id,
          username: character.username,
          display_name: character.display_name,
          tagline: character.tagline,
          icon: character.icon,
          author: character.author,
          visibility: character.visibility
        };
        
        navigate(`/chats-preview/${character.username}`, { 
          state: { previewCharacter: serializableCharacter } 
        });
        return;
      }
      
      // User is logged in, create a new chat
      if (!character) return;
      const newChat = await api.chats.create(character.id);
      navigate(`/chats/${newChat.id}`);
    } catch (error) {
      console.error('Failed to start chat:', error);
    }
  };

  useEffect(() => {
    if (shouldAutostart && character && user) {
      handleStartChat();
    }
  }, [shouldAutostart, character, user]);

  useEffect(() => {
    if (USE_TEST_DATA) {
      setCharacter(TEST_DATA as CharacterType);
      setLoading(false);
      return;
    }

    const fetchCharacter = async (): Promise<void> => {
      try {
        if (!username) {
          throw new Error("No username provided");
        }
        const characterObj = await api.characters.get(username);
        setCharacter(characterObj);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (username) {
      fetchCharacter();
    } else {
      setError("No username provided");
      setLoading(false);
    }
  }, [username]);

  const handleIconChange = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUpdating(true);
    try {
      // Create a temporary preview URL
      const tempUrl = URL.createObjectURL(file);
      
      // Update the UI with a temporary image preview
      setCharacter(prev => {
        if (!prev) return null;
        return {
          ...prev,
          icon: tempUrl
        } as CharacterType;
      });

      // Make the actual API call - safeUpdate will update the character state
      await safeUpdate({ icon: file });
      
      // Clean up the temporary URL
      URL.revokeObjectURL(tempUrl);
    } catch (error) {
      console.error('Failed to update character icon:', error);
      
      // Revert to the previous icon if there's an error
      if (character) {
        setCharacter(prev => {
          if (!prev) return null;
          return {
            ...prev,
            icon: character.icon
          } as CharacterType;
        });
      }
    } finally {
      setIsUpdating(false);
    }
  };

  if (loading) {
    return <ProfileLoading />;
  }
  
  if (error) return <div className={styles.container}>Error: {error}</div>;
  if (!character) return <div className={styles.container}>No character found</div>;

  return (
    <div className={styles.wrapper}>
      <div 
        className={styles.backgroundImage} 
        style={{ 
          backgroundImage: `url(${isUpdating ? character.icon : (character.icon || '/hangjam.png')})`,
          transition: 'filter 0.3s ease'
        }} 
      />
      <div 
        className={`${styles.container} ${activeTab === 'edit' ? styles.editModeContainer : ''}`} 
      >
        <div className={styles.headerButtons}>
          {isCurrentAuthor && (
            <>
              {activeTab === 'edit' ? (
                <button 
                  className={`${styles.topRightEditButton} ${styles.doneButton}`}
                  onClick={() => setActiveTab('about')}
                  aria-label="Done editing"
                >
                  <span className={styles.editIconSymbol}>
                    <FiCheck size={20} />
                  </span>
                  <span className={styles.buttonText}>
                    Done
                  </span>
                </button>
              ) : (
                <button 
                  className={styles.topRightEditButton}
                  onClick={() => setActiveTab('edit')}
                  aria-label="Edit character"
                >
                  <span className={styles.editIconSymbol}>
                    <FiEdit size={20} />
                  </span>
                  <span className={styles.buttonText}>
                    Edit
                  </span>
                </button>
              )}
            </>
          )}
          <ShareButton username={character.username} type="character" />
        </div>
        
        <CharacterProfileHeader 
          character={character}
          displayName={displayName}
          tagline={tagline}
          characterIcon={characterIcon}
          isUpdating={isUpdating}
          handleStartChat={handleStartChat}
          handleIconChange={handleIconChange}
        />
        
        <CharacterProfileTabs
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          character={character}
          isCurrentAuthor={isCurrentAuthor}
          isEditMode={isEditMode}
          openModal={openModal}
          chat={chat || undefined}
          characterVisibilityState={characterVisibilityState}
          handleVisibilityChange={handleVisibilityChange}
          isUpdatingVisibility={isUpdatingVisibility}
          visibilityError={visibilityError}
        />
      </div>

      <CharacterProfileModals 
        modalState={{
          ...modalState,
          mode: modalState.mode || 'view', // Provide a default mode if it's null
          error: modalState.error || null,  // Convert empty string to null
          config: modalState.config || undefined // Convert null to undefined
        }}
        closeModal={closeModal}
        handleModalSubmit={handleModalSubmit}
        handleModalValueChange={handleModalValueChange}
        displayName={displayName}
        tagline={tagline}
        notification={notification}
        setNotification={setNotification}
      />
    </div>
  );
};

export default CharacterProfile; 